import store from "@/store";
import { InspectKbn, SeibiKbn } from "@/model/InspectKbn";
import { InspectStaff } from "@/model/InspectStaff";
import { LiftUse } from "@/model/LiftUse";
import { InspectDetail } from "@/model/InspectDetail";
import { InspectionDetailState } from "@/store/modules/inspectionDetailStore";
import { IppanSeibiService } from "@/service/IppanSeibiService";

const namespace = "InspectionDetail";

export class InspectionJizenDetailStoreService {
  private ippanSeibiService: IppanSeibiService;

  // サービス初期化時に事前見積の選択管理枠をStoreへ登録する
  constructor() {
    this.ippanSeibiService = new IppanSeibiService();
    this.addNowSelected();
  }

  // InspectionDetail Store全体の現在状態を参照する
  get currentState(): InspectionDetailState {
    return store.getters[`${namespace}/currentState`];
  }

  // 事前見積が選択済みかどうかを参照する
  get nowSelected(): boolean {
    const nowSelected = store.getters[`${namespace}/getNowSelected`];
    if (nowSelected.jizen === true || nowSelected.jizen === false) {
      return nowSelected.jizen;
    }
    this.addNowSelected();
    return false;
  }

  // データ参照用に追加 2026/07/02
  // 事前見積一覧の編集用データを参照する
  get jizenState(): InspectKbn[] {
    return this.currentState.jizen;
  }

  // 事前見積を実施する選択かどうかを参照する
  get isDoJizen(): boolean {
    return store.getters[`${namespace}/isDoJizen`];
  }

  // 事前見積の選択管理枠をStoreへ追加する
  addNowSelected() {
    return store.dispatch(`${namespace}/addNowSelected`, "jizen");
  }

  // 事前見積の選択済み状態をStoreへ反映する
  setNowSelected(payload: boolean) {
    return store.dispatch(`${namespace}/setNowSelected`, {
      key: "jizen",
      value: payload
    });
  }

  // 事前見積の選択状態からスタート可否判定用の状態を更新する
  checkDisableStart(arrData: any, isUnSelect?: boolean) {
    let checked: boolean = false;
    if (isUnSelect) {
      checked = true;
    } else if (arrData) {
      checked = arrData.some((data: any) => data.selected == "1");
    }
    this.setNowSelected(checked);
  }

  // 事前見積一覧をStoreへ反映する
  setJizen(payload: InspectKbn[]) {
    return store.dispatch(`${namespace}/setJizen`, payload);
  }

  // 保存対象の事前見積コードをStoreへ反映する
  setJizenCd(payload: string | number) {
    return store.dispatch(`${namespace}/setJizenCd`, payload);
  }

  // 共通処理から扱う事前見積一覧を返す
  get list(): InspectKbn[] {
    return this.jizenState;
  }

  // 共通処理から事前見積一覧をStoreへ反映する
  setList(payload: InspectKbn[]) {
    return this.setJizen(payload);
  }

  // 共通処理から事前見積コードをStoreへ反映する
  setCd(payload: string | number) {
    return this.setJizenCd(payload);
  }

  // テンプレート表示用の事前見積選択肢リストを生成する
  getTargetList() {
    return this.list.map((data: any) => {
      return {
        "mode": 1,
        "btnDel": false,
        "btnSel": data.selected == '1',
        "btnFre": data.selected == '0',
        "btnUse": false,
        "key": data.kbnKey,
        "dataSet": data.kbnKey,
        "isDisabled": data.isDisabled == 1,
        "icon": "icon-line",
        "label": data.value,
        "labelEntends1": null,
        "labelEntends2": null,
        "labelEntends3": null,
      };
    });
  }

  // スタート時に選択中の事前見積を保存データへ反映する
  setStartInspectDetail(inspectionDetail: InspectDetail) {
    const selectedData = this.list.find((data: InspectKbn) => {
      return data.selected == "1";
    });
    if (!selectedData) { return; }

    inspectionDetail.jizenCd = String(selectedData.kbnKey);
    inspectionDetail.jizenNm = selectedData.value;
    this.setCd(selectedData.kbnKey);
  }

  // 取得済みの検査構成から事前見積の初期選択情報を反映する
  applyInitialInspectionDetail(
    data: any,
    inspectionDetail: InspectDetail,
    isCanJizenMitsumori: boolean | number | null = null
  ) {
    if (this.ippanSeibiService.isIppanSeibi) {
      // 一般整備の場合は事前見積「する」固定
      inspectionDetail.jizenCd = "1";
    }
    if (isCanJizenMitsumori && inspectionDetail.jizenCd == "") {
      // 事前見積を利用する場合、未設定なら「する」を初期値にする
      inspectionDetail.jizenCd = "1";
    }
    // 将来的にこの記述も'1'にした方が良いと思われる（変更もれなのかどうか未検証）
    this.setCd(data.jizenCd);
  }

  // 事前見積の選択クリックを処理する
  select(kbnKey: number) {
    const jizen = this.list;
    jizen.map(data => {
      if (data.kbnKey == kbnKey) {
        if (data.selected == "1") {
          data.selected = "0";
        } else {
          data.selected = "1";
        }
      } else {
        data.selected = "0";
      }
    });
    this.setList([...jizen]);
    this.checkDisableStart(jizen);
  }

  // 事前見積で「選択無し」を表示できるか返す
  canDeSelect() {
    return false;
  }

  // 事前見積が「選択無し」状態か返す
  isDeSelected() {
    return null;
  }

  // 共通呼び出し用の「選択無し」クリック処理
  clickDeSelect() {
    // ダミー
    return false;
  }

  // Storeに既存リストがある場合に選択状態を再評価する
  applyExistingList() {
    const jizen = this.list;
    this.setList(jizen);
    this.checkDisableStart(jizen);
    return jizen;
  }

  // API取得結果から事前見積一覧の初期選択状態を作成する
  setInitialList(res: InspectKbn[], jizenCd: string | number | undefined) {
    const jizen = res.map(data => {
      if (jizenCd && +jizenCd == data.kbnKey) {
        data.selected = "1";
        this.setNowSelected(true);
      } else {
        data.selected = "0";
      }
      if (this.ippanSeibiService.isIppanSeibi) {
        // 一般整備の場合は事前見積「する」固定
        data.isDisabled = 1;
      }
      return data;
    });
    this.setList(jizen);
    return jizen;
  }

  // 事前見積一覧を既存StoreまたはAPIから取得する
  getDataList(
    loadInitialList: () => Promise<InspectKbn[]>,
    jizenCd: string | number | undefined
  ) {
    if (this.list.length > 0) {
      this.applyExistingList();
      return {
        hasExistingList: true,
        promise: null
      };
    }
    return {
      hasExistingList: false,
      promise: loadInitialList().then((res: InspectKbn[]) => {
        return this.setInitialList(res, jizenCd);
      })
    };
  }
}
